'use client';

import { createTheme } from '@mui/material/styles';

export const MONO_FONT =
  "var(--font-google-sans-code), 'JetBrains Mono', ui-monospace, 'SFMono-Regular', Consolas, monospace";
export const SANS_FONT =
  "var(--font-open-sans), system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

// Oculta un elemento visualmente pero lo deja en el árbol de accesibilidad (lectores de pantalla).
// Uso: <Box component="h2" sx={VISUALLY_HIDDEN}>…</Box>. Sirve para dar jerarquía de encabezados
// (h1 → h2 → h3) a cuadrículas de tarjetas sin añadir un título visible.
export const VISUALLY_HIDDEN = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: '1px',
};

const theme = createTheme({
  palette: {
    primary: {
      main: '#009f8b',
      dark: '#00866f',
      darker: '#006b59',
      light: '#d6efe9',
      lightest: '#ecf8f4',
      glow: '#1abf9f',
      lighterBg: '#d8f0e8',
      darkest: '#00543f',
      lightAccent: '#7ed7c5',
      contrastText: '#ffffff',
    },
    text: {
      primary: '#1f2422',
      secondary: '#444a47',
      disabled: '#9aa09c',
      // Variables CSS (globals.scss): teal / gris que cumplen contraste AA según la superficie.
      accent: 'var(--c-accent)',
      muted: 'var(--c-muted)',
    },
    background: {
      default: '#ffffff',
      paper: '#f6f7f5',
    },
    grey: {
      950: '#0a0f0e',
      900: '#14191a',
      800: '#1f2422',
      700: '#2a302d',
      600: '#444a47',
      500: '#6b716e',
      400: '#9aa09c',
      300: '#c8ccc9',
      200: '#e2e5e3',
      100: '#eef0ed',
      50: '#f6f7f5',
    },
    warning: {
      main: '#d97706',
    },
    error: {
      main: '#b91c1c',
    },
  },
  typography: {
    fontFamily: SANS_FONT,
    fontFamilyMono: MONO_FONT,
    fontSize: 16,
    h1: {
      fontSize: 'clamp(48px, 7vw, 96px)',
      fontWeight: 400,
      lineHeight: 1.05,
      letterSpacing: '-0.03em',
    },
    h2: {
      // Tope en 48px (≈ 3.5vw a ~1370px): el contenedor no pasa de 1240px, así que sobre ese ancho el
      // título solo ganaría líneas. Así se ve igual en un laptop que en un monitor grande.
      fontSize: 'clamp(40px, 3.5vw, 48px)',
      fontWeight: 400,
      lineHeight: 1.1,
      letterSpacing: '-0.025em',
    },
    h3: {
      fontSize: 'clamp(22px, 2.5vw, 28px)',
      fontWeight: 600,
      lineHeight: 1.25,
      letterSpacing: '-0.015em',
    },
    h4: {
      fontSize: '18px',
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h5: {
      fontSize: '16px',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h6: {
      fontSize: '15px',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    body1: {
      fontSize: '16px',
      lineHeight: 1.55,
      letterSpacing: '-0.005em',
    },
    body2: {
      fontSize: '14px',
      lineHeight: 1.55,
      letterSpacing: '-0.005em',
    },
    caption: {
      fontSize: '12px',
      lineHeight: 1.4,
      color: '#6b716e',
    },
  },
  spacing: 8,
  shape: {
    borderRadius: 4,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '*, *::before, *::after': { boxSizing: 'border-box' },
        html: { fontSize: '16px', scrollBehavior: 'smooth' },
        body: {
          fontFamily: SANS_FONT,
          color: '#1f2422',
          background: '#ffffff',
          overflowX: 'hidden',
        },
        'img, video': { display: 'block', maxWidth: '100%' },
        a: { color: 'inherit', textDecoration: 'none' },
        button: { font: 'inherit', cursor: 'pointer' },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          fontFamily: SANS_FONT,
          fontWeight: 600,
          fontSize: '14px',
          letterSpacing: '0',
          textTransform: 'none',
          borderRadius: '4px',
          padding: '10px 20px',
          lineHeight: 1.4,
          transition: 'background 0.15s, color 0.15s, border-color 0.15s',
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
          ...(ownerState.size === 'small' && {
            padding: '6px 14px',
            fontSize: '13px',
          }),
          ...(ownerState.size === 'large' && {
            padding: '14px 28px',
            fontSize: '15px',
          }),
          ...(ownerState.variant === 'contained' &&
            ownerState.color === 'primary' && {
              backgroundColor: '#00866f',
              color: '#ffffff',
              '&:hover': { backgroundColor: '#006b59' },
            }),
          ...(ownerState.variant === 'outlined' &&
            ownerState.color === 'primary' && {
              borderColor: '#00866f',
              borderWidth: '1.5px',
              borderStyle: 'solid',
              color: '#006b59',
              backgroundColor: 'transparent',
              '&:hover': { backgroundColor: '#ecf8f4', borderColor: '#00866f' },
            }),
          ...(ownerState.variant === 'text' && {
            color: '#006b59',
            padding: '0',
            '&:hover': {
              backgroundColor: 'transparent',
              textDecoration: 'underline',
            },
          }),
        }),
      },
      defaultProps: {
        disableElevation: true,
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: '1px solid #eef0ed',
          borderRadius: '6px',
          boxShadow: 'none',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #eef0ed',
          minHeight: '40px',
        },
        indicator: {
          backgroundColor: '#009f8b',
          height: '2px',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily: MONO_FONT,
          fontSize: '11px',
          fontWeight: 500,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: '#6b716e',
          minHeight: '40px',
          padding: '8px 16px',
          '&.Mui-selected': { color: '#1f2422' },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: MONO_FONT,
          fontSize: '11px',
          fontWeight: 500,
          letterSpacing: '0.06em',
          height: '28px',
          borderRadius: '4px',
        },
        colorPrimary: {
          background: '#ecf8f4',
          color: '#009f8b',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '4px',
            fontSize: '14px',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#9aa09c',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#009f8b',
              borderWidth: '1.5px',
              boxShadow: '0 0 0 3px rgba(0,159,139,0.12)',
            },
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: '#009f8b',
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: '#eef0ed',
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: '#009f8b',
          textDecoration: 'none',
          '&:hover': { textDecoration: 'underline' },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          background: 'transparent',
          boxShadow: 'none',
          borderBottom: '1px solid #e2e5e3',
          '&::before': { display: 'none' },
          '&.Mui-expanded': { margin: 0 },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          padding: '0',
          minHeight: 'unset',
          '&.Mui-expanded': { minHeight: 'unset' },
        },
        content: {
          display: 'grid',
          gridTemplateColumns: '48px 1fr',
          alignItems: 'center',
          gap: '12px',
          margin: '22px 0',
          '&.Mui-expanded': { margin: '22px 0' },
        },
        expandIconWrapper: {
          color: '#9aa09c',
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: '0 0 24px 60px',
          fontSize: '15px',
          lineHeight: 1.65,
          color: '#444a47',
        },
      },
    },
  },
});

export default theme;
